import { Box, Container, Divider, Grid, Link, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import MinusIcon from "@mui/icons-material/Remove";
import { GetBlockResult } from "upcx-js/dist/upcxjs-rpc-interfaces";
import { useMemo, useState } from "react";
import { CalculatePassTime, convertTimestampToLocal } from "../../utils";
import { useRecoilState } from "recoil";
import { ThemeLight } from "../../state/ThemeLight";

interface Props {
  block?: GetBlockResult;
}

const Overview = ({ block }: Props) => {
  const [themeLight] = useRecoilState(ThemeLight);

  const currentTime = useMemo(() => new Date(block?.timestamp ?? ""), [block]);
  const passTime = useMemo(() => {
    return CalculatePassTime(currentTime);
  }, [currentTime]);
  const [visibleShowMore, setVisibleShowMore] = useState(true);

  return (
    <Container className="container-xxl my-2">
      <Box className="card p-4">
        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Block Height:
          </Grid>
          <Grid item xs={9} className="">
            <Box className="d-flex align-items-center gap-2">
              <Typography>{block?.block_num}</Typography>
              <Box className="text-nowrap d-flex gap-1">
                <Link
                  className="btn btn-sm btn-secondary py-0.5"
                  href={`/block/${(block?.block_num ?? 1) - 1}`}
                  data-bs-toggle="tooltip"
                  data-bs-trigger="hover"
                  data-bs-placement="top"
                  aria-label="View previous block"
                >
                  <ChevronLeftIcon
                    fontSize="small"
                    sx={themeLight ? {} : { color: "white !important" }}
                  />
                </Link>
                <Link
                  className="btn btn-sm btn-secondary py-0.5"
                  href={`/block/${(block?.block_num ?? 1) + 1}`}
                  data-bs-toggle="tooltip"
                  data-bs-trigger="hover"
                  data-bs-placement="top"
                  aria-label="View previous block"
                >
                  <ChevronRightIcon fontSize="small" />
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Status:
          </Grid>
          <Grid item xs={9} className="text-dt">
            <Box className="d-flex align-items-center gap-2">
              <span
                className="badge bg-success bg-opacity-10 border border-success border-opacity-25 text-success fw-medium py-1 px-2 font-normal d-flex align-items-center"
                data-bs-toggle="tooltip"
                data-bs-trigger="hover"
              >
                {block?.confirmed === 0 ? (
                  <CheckIcon fontSize="medium" className="text-icon" />
                ) : (
                  <HourglassTopIcon fontSize="small" className="text-icon" />
                )}
                {block?.confirmed === 0 ? "Confirmed" : "Unconfirmed"}
              </span>
            </Box>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Timestamp:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              <AccessTimeIcon fontSize="small" />
              {passTime} ago (
              <span id="showUtcLocalDate" data-timestamp="1705414331">
                {convertTimestampToLocal(currentTime).toUTCString()}
              </span>
              )
            </Box>
          </Grid>
        </Grid>

        {/* <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Proposed On:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              Block proposed on slot{" "}
              <Link href="/slot/8215859" target="_blank">
                8215859
              </Link>
              , epoch{" "}
              <Link href="/epoch/256745" target="_blank">
                256745
              </Link>
            </Box>
          </Grid>
        </Grid> */}

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Transactions:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              <Link href="/txs?block=8215859" target="_blank">
                {block?.transactions.length} transaction
                {block?.transactions.length > 0 ? "s" : ""}
              </Link>
              in this block
            </Box>
          </Grid>
        </Grid>

        {/* <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Withdrawals:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              <Link href="/txs?block=8215859" target="_blank">
                16 withdrawals
              </Link>
              in this block
            </Box>
          </Grid>
        </Grid> */}

        <Divider />

        <Grid container className="mt-4 mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Fee Recipient:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              <Link href={`/address/${block?.producer}`} target="_blank">
                {block?.producer}
              </Link>
              <ContentCopyIcon
                fontSize="small"
                className="text-icon"
                onClick={() =>
                  navigator.clipboard.writeText(block?.producer ?? "")
                }
              />
              in 500ms
            </Box>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Block Reward:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">0.00 UPC</Box>
          </Grid>
        </Grid>

        {/* <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Total Difficulty:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              58,750,003,716,598,352,816,469
            </Box>
          </Grid>
        </Grid> */}

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Size:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              {JSON.stringify(block ?? {}).length.toLocaleString()} bytes
            </Box>
          </Grid>
        </Grid>

        {/* <Divider />

        <Grid container className="mb-4 mt-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Gas Used:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              0.00
              <span className="text-muted"> (0.00%)</span>
              <img src="/assets/img/graph.svg" alt="Graph SVG" />
              <span id="gasTargetText" className="text-danger">
                0% Gas Target
              </span>
            </Box>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Gas Limit:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">30,000,000</Box>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Base Fee Per Gas:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              0.00000004013773394 UPC
              <span className="text-muted">
                (40<b>.</b>13773394 Gwei)
              </span>
            </Box>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Burnt Fees:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              🔥 0.5448906098571488 UPC
            </Box>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Extra Data:
          </Grid>
          <Grid item xs={9}>
            beaverbuild.org (Hex:0x6265617665726275696c642e6f7267)
          </Grid>
        </Grid> */}
      </Box>

      <Box className="card p-4 mt-4">
        <div className="expand-container">
          <div className={`expand-contract ${visibleShowMore && "expanded"}`}>
            <Grid container className="mb-4">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                <HelpOutlineIcon fontSize="small" className="me-1" />
                Hash:
              </Grid>
              <Grid item xs={9}>
                {block?.id}
              </Grid>
            </Grid>

            <Grid container className="mb-4">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                <HelpOutlineIcon fontSize="small" className="me-1" />
                Previous Hash:
              </Grid>
              <Grid item xs={9}>
                <Link href={`/block/${(block?.block_num ?? 1) - 1}`}>
                  {block?.previous}
                </Link>
              </Grid>
            </Grid>

            <Grid container className="mb-4">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                <HelpOutlineIcon fontSize="small" className="me-1" />
                StateRoot:
              </Grid>
              <Grid item xs={9}>
                {block?.action_mroot}
              </Grid>
            </Grid>

            {/* <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            WithdrawalsRoot:
          </Grid>
          <Grid item xs={9}>
            0x9bf0863ea4eb8da82a7dc220e846a14cd3b6d38a4a80f570c30142299f8b5b5c
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Nonce:
          </Grid>
          <Grid item xs={9}>
            0x0000000000000000
          </Grid>
        </Grid> */}

            <Divider />
          </div>
        </div>
        <Grid container className={`mb-2 mt-${visibleShowMore ? 4 : 2}`}>
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            More Detail:
          </Grid>
          <Grid item xs={9}>
            <Link
              className="d-flex align-items-center"
              style={{
                cursor: "pointer",
              }}
              onClick={() => setVisibleShowMore(!visibleShowMore)}
            >
              {!visibleShowMore ? (
                <AddIcon className="text-icon" />
              ) : (
                <MinusIcon className="text-icon" />
              )}
              &nbsp; Click to show {visibleShowMore ? "less" : "more"}
            </Link>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Overview;
